"use client";

import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { useSession } from "next-auth/react";
import ConfirmDialog from "@/app/components/modals/ConfirmDialog";
import useActionNotifier from "@/lib/helpers/useActionNotifier";
import {
  markActionNotified,
  setNotificationShownInSession,
  setNotificationSnooze,
  clearNotificationSnooze,
  markReviewPromptShown,
} from "@/store/apps/notifier/notifierDataSlice";
import moment from "moment";
import VerifyIdentityModal from "@/app/components/modals/verify/VerifyIdentityModal";
import ReceiveModal from "@/app/components/modals/receive/ReceiveModal";
import ScheduleModal from "@/app/components/modals/schedule/ScheduleModal";
import ReviewSubmissionModal from "@/app/components/modals/review/ReviewSubmissionModal";
import { getCookie, setCookie, deleteCookie } from "cookies-next";

const REVIEW_SNOOZE_DURATION_MS = 7 * 24 * 60 * 60 * 1000;
const REVIEW_NEVER_ASK_COOKIE = "reviewNotifierNeverAsk";

const normalizeRole = (role) => {
  const r = (role || "").toLowerCase();
  if (r.includes("sharer")) return "sharer";
  if (r.includes("seeker")) return "seeker";
  return r;
};

const getReviewPromptName = (person) => {
  const title = (person?.title || "").trim();
  const givenName = (person?.given_name || "").trim();
  const familyName = (person?.family_name || "").trim();
  const fullName = [title, givenName, familyName].filter(Boolean).join(" ");

  return fullName || "them";
};

function verifyUserRoleInMeeting(meeting, userSub, status) {
  if (!meeting || !userSub) return false;

  const isSeeker = meeting.seeker_sub === userSub;
  const isSharer = meeting.sharer_sub === userSub;

  status = status?.toUpperCase();

  if (["DRAFT", "ACCEPTED"].includes(status)) {
    return isSeeker;
  } else if (status === "REQUESTED") {
    return isSharer;
  } else if (status === "LAUNCHING") {
    return isSeeker || isSharer;
  } else if (status === "COMPLETED_NEEDS_REVIEW") {
    return isSeeker;
  }

  return false;
}

const ActionNotifier = ({
  hasOtherNotifications = false,
  onActiveChange = () => {},
}) => {
  const [mounted, setMounted] = useState(false);
  const [hideModal, setHideModal] = useState(false);
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const dispatch = useDispatch();
  const router = useRouter();
  const { data: session } = useSession();

  const [open, setOpen] = useState(false);
  const [notificationData, setNotificationData] = useState(null);
  const [additionalCount, setAdditionalCount] = useState(0);
  const [requiresRoleSwitch, setRequiresRoleSwitch] = useState(false);
  const isUpdatingRoleRef = useRef(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Keep a ref so the 300ms setTimeout callback can check the *current*
  // value of hasOtherNotifications at fire time, not the stale closure value.
  const hasOtherNotificationsRef = useRef(hasOtherNotifications);
  useEffect(() => {
    hasOtherNotificationsRef.current = hasOtherNotifications;
  }, [hasOtherNotifications]);

  const [snoozeChecked, setSnoozeChecked] = useState(false);

  const [verifyModalOpen, setVerifyModalOpen] = useState(false);
  const [receiveModalOpen, setReceiveModalOpen] = useState(false);
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [currentMeetingData, setCurrentMeetingData] = useState(null);

  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reviewMeetingData, setReviewMeetingData] = useState(null);

  const [isReviewSnoozed, setIsReviewSnoozed] = useState(false);
  const [isReviewReminderDisabled, setIsReviewReminderDisabled] =
    useState(false);

  const [reviewPromptCooldown, setReviewPromptCooldown] = useState(false);

  const { pendingActions, getNextAction, getActionsRequiringRoleSwitch } =
    useActionNotifier();
  const { role: currentAppRole } = useSelector((state) => state.user);
  const meetings = useSelector((state) => state.meetings.data);
  const { isLoading } = useSelector((state) => state.loading);
  const notificationShownInSession = useSelector(
    (state) => state.notifications.notificationShownInSession || [],
  );
  const isNotificationsSnoozed = useSelector(
    (state) => state.notifications.isNotificationsSnoozed,
  );
  const isRoleLoaded = currentAppRole !== null && currentAppRole !== undefined;

  const [tabWasHidden, setTabWasHidden] = useState(false);
  const notificationTimeoutRef = useRef(null);

  useEffect(() => {
    onActiveChange(
      open ||
        verifyModalOpen ||
        receiveModalOpen ||
        scheduleModalOpen ||
        reviewModalOpen,
    );
  }, [
    open,
    verifyModalOpen,
    receiveModalOpen,
    scheduleModalOpen,
    reviewModalOpen,
    onActiveChange,
  ]);

  useEffect(() => {
    const snoozedUntil = getCookie("actionNotifierSnoozed");
    if (snoozedUntil) {
      const timestamp = parseInt(snoozedUntil);
      if (timestamp > Date.now()) {
        dispatch(setNotificationSnooze(timestamp));
      } else {
        deleteCookie("actionNotifierSnoozed");
        dispatch(clearNotificationSnooze());
      }
    }

    const reviewSnoozedUntil = getCookie("reviewNotifierSnoozed");
    if (reviewSnoozedUntil) {
      const timestamp = parseInt(reviewSnoozedUntil);
      if (timestamp > Date.now()) {
        setIsReviewSnoozed(true);
      } else {
        deleteCookie("reviewNotifierSnoozed");
        setIsReviewSnoozed(false);
      }
    }

    const reviewNeverAsk = getCookie(REVIEW_NEVER_ASK_COOKIE);
    setIsReviewReminderDisabled(reviewNeverAsk === "1");
  }, [dispatch]);

  useEffect(() => {
    function handleVisibilityChange() {
      if (document.hidden) {
        setTabWasHidden(true);
      }
    }

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (notificationTimeoutRef.current) {
        clearTimeout(notificationTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (mounted) {
      const idParam = searchParams.get("id");
      if (idParam) {
        setHideModal(true);
      }
    }
  }, [mounted, searchParams]);

  useEffect(() => {
    if (
      !session?.user?.sub ||
      isLoading ||
      !meetings?.length ||
      !pendingActions.length ||
      !isRoleLoaded ||
      pathname === "/video" ||
      tabWasHidden
    ) {
      return;
    }

    const nextAction = getNextAction();

    if (nextAction) {
      const meetingData = nextAction.meetingData;
      const userSub = session?.user?.sub;
      const isUserCorrectParticipant = verifyUserRoleInMeeting(
        meetingData,
        userSub,
        nextAction.status,
      );

      if (!isUserCorrectParticipant) {
        return;
      }

      if (notificationShownInSession.includes(nextAction.id)) {
        return;
      }

      const isReviewAction =
        nextAction.status?.toUpperCase() === "COMPLETED_NEEDS_REVIEW";

      if (isNotificationsSnoozed || hasOtherNotifications) {
        // Clear any pending timeout so a stale callback queued before
        // hasOtherNotifications became true doesn't open the modal anyway.
        if (notificationTimeoutRef.current) {
          clearTimeout(notificationTimeoutRef.current);
          notificationTimeoutRef.current = null;
        }
        return;
      }

      if (isReviewAction && isReviewSnoozed) {
        if (notificationTimeoutRef.current) {
          clearTimeout(notificationTimeoutRef.current);
          notificationTimeoutRef.current = null;
        }
        return;
      }

      if (isReviewAction && isReviewReminderDisabled) {
        if (notificationTimeoutRef.current) {
          clearTimeout(notificationTimeoutRef.current);
          notificationTimeoutRef.current = null;
        }
        return;
      }

      if (isReviewAction && reviewPromptCooldown) {
        if (notificationTimeoutRef.current) {
          clearTimeout(notificationTimeoutRef.current);
          notificationTimeoutRef.current = null;
        }
        return;
      }

      const normalizedCurrentRole = normalizeRole(currentAppRole);
      const needsRoleSwitch =
        nextAction.requiredRole !== "both" &&
        nextAction.requiredRole !== normalizedCurrentRole;

      if (notificationTimeoutRef.current) {
        clearTimeout(notificationTimeoutRef.current);
      }

      notificationTimeoutRef.current = setTimeout(() => {
        // Re-check with the ref — hasOtherNotifications may have become
        // true after the timeout was queued but before it fired.
        if (hasOtherNotificationsRef.current) {
          notificationTimeoutRef.current = null;
          return;
        }
        setNotificationData(nextAction);
        setRequiresRoleSwitch(needsRoleSwitch);
        const totalPending = pendingActions.length;
        if (totalPending > 1) {
          setAdditionalCount(totalPending - 1);
        }
        setOpen(true);
        dispatch(
          setNotificationShownInSession([
            ...notificationShownInSession,
            nextAction.id,
          ]),
        );
        dispatch(markActionNotified(nextAction.id));
      }, 300);
    }
  }, [
    session,
    meetings,
    pendingActions,
    currentAppRole,
    isLoading,
    dispatch,
    notificationShownInSession,
    isRoleLoaded,
    getNextAction,
    pathname,
    tabWasHidden,
    isNotificationsSnoozed,
    hasOtherNotifications,
    isReviewSnoozed,
    isReviewReminderDisabled,
    reviewPromptCooldown,
  ]);

  const shouldRenderModal =
    mounted &&
    !hideModal &&
    notificationData &&
    isRoleLoaded &&
    pathname !== "/video";

  const handleClose = () => {
    setSnoozeChecked(false);
    setOpen(false);
  };

  const handleSnooze = useCallback(() => {
    const snoozeUntil = Date.now() + 24 * 60 * 60 * 1000;
    setCookie("actionNotifierSnoozed", snoozeUntil.toString(), {
      maxAge: 60 * 60 * 24, // 24 hours
      secure: process.env.NODE_ENV === "production",
      sameSite: "lax",
    });
    dispatch(setNotificationSnooze(snoozeUntil));
    setSnoozeChecked(false);
    setOpen(false);
  }, [dispatch]);

  const handleReviewSnooze = useCallback(() => {
    const snoozeUntil = Date.now() + REVIEW_SNOOZE_DURATION_MS;

    // Review-specific snooze only.
    setCookie("reviewNotifierSnoozed", snoozeUntil.toString(), {
      maxAge: 60 * 60 * 24 * 7,
      secure: process.env.NODE_ENV === "production",
      sameSite: "lax",
    });
    setIsReviewSnoozed(true);

    if (notificationData?.meetingData?.id) {
      dispatch(markReviewPromptShown(notificationData.meetingData.id));
    }

    setReviewPromptCooldown(true);

    setSnoozeChecked(false);
    setOpen(false);
  }, [dispatch, notificationData]);

  const applyReviewSnooze = useCallback(() => {
    const snoozeUntil = Date.now() + REVIEW_SNOOZE_DURATION_MS;

    setCookie("reviewNotifierSnoozed", snoozeUntil.toString(), {
      maxAge: 60 * 60 * 24 * 7,
      secure: process.env.NODE_ENV === "production",
      sameSite: "lax",
    });

    setIsReviewSnoozed(true);
    setReviewPromptCooldown(true);
  }, []);

  const handleReviewNeverAskAgain = useCallback(() => {
    setCookie(REVIEW_NEVER_ASK_COOKIE, "1", {
      maxAge: 60 * 60 * 24 * 365 * 5,
      secure: process.env.NODE_ENV === "production",
      sameSite: "lax",
    });

    deleteCookie("reviewNotifierSnoozed");
    setIsReviewSnoozed(false);
    setIsReviewReminderDisabled(true);

    if (notificationData?.meetingData?.id) {
      dispatch(markReviewPromptShown(notificationData.meetingData.id));
    }

    setReviewPromptCooldown(true);
    setSnoozeChecked(false);
    setOpen(false);
  }, [dispatch, notificationData]);

  const handleReviewModalClose = () => {
    applyReviewSnooze();
    setReviewModalOpen(false);
    setReviewMeetingData(null);
  };

  const handleReviewSubmitted = () => {
    setReviewModalOpen(false);
    setReviewMeetingData(null);

    if (notificationData?.meetingData?.id) {
      dispatch(markReviewPromptShown(notificationData.meetingData.id));
    }

    setReviewPromptCooldown(true);
  };

  const handleAction = async () => {
    if (!notificationData || isUpdatingRoleRef.current || actionLoading) return;

    const status = notificationData.status?.toUpperCase();

    setActionLoading(true);
    isUpdatingRoleRef.current = true;

    if (status === "COMPLETED_NEEDS_REVIEW") {
      const meeting = notificationData.meetingData;
      const isSeeker = meeting.seeker_sub === session.user.sub;
      const otherPerson = isSeeker ? meeting.sharer : meeting.seeker;

      setReviewPromptCooldown(true);

      if (otherPerson && otherPerson.given_name) {
        const fullName = getReviewPromptName(otherPerson);

        setReviewMeetingData({
          meetingId: meeting.id,
          sharerId: otherPerson.sub,
          sharerName: fullName,
        });

        setOpen(false);
        setReviewModalOpen(true);
        dispatch(markReviewPromptShown(meeting.id));
      }

      setActionLoading(false);
      isUpdatingRoleRef.current = false;
      return;
    }

    if (status === "DRAFT") {
      setCurrentMeetingData(notificationData.meetingData);
      setOpen(false);
      setVerifyModalOpen(true);
      setActionLoading(false);
      isUpdatingRoleRef.current = false;
      return;
    }

    if (status === "REQUESTED") {
      setCurrentMeetingData(notificationData.meetingData);
      setOpen(false);
      setReceiveModalOpen(true);
      setActionLoading(false);
      isUpdatingRoleRef.current = false;
      return;
    }

    if (status === "ACCEPTED") {
      setCurrentMeetingData(notificationData.meetingData);
      setOpen(false);
      setScheduleModalOpen(true);
      setActionLoading(false);
      isUpdatingRoleRef.current = false;
      return;
    }

    if (status === "LAUNCHING") {
      const destination = `/video?id=${notificationData.meetingData.request_id}`;
      router.push(destination);
      isUpdatingRoleRef.current = false;
      return;
    }

    const shouldScrollToBottom =
      pathname === "/" && ["ACCEPTED", "REQUESTED"].includes(status);

    let destination = "/";
    if (pathname.startsWith("/meetings")) {
      destination = "/meetings";
    }

    setOpen(false);

    setTimeout(() => {
      if (shouldScrollToBottom) {
        const scrollToBottom = () => {
          const documentHeight = Math.max(
            document.body.scrollHeight,
            document.documentElement.scrollHeight,
          );

          window.scrollTo({
            top: documentHeight,
            behavior: "smooth",
          });

          setTimeout(() => {
            if (window.scrollY < documentHeight - window.innerHeight - 50) {
              document.documentElement.scrollTop = documentHeight;
              document.body.scrollTop = documentHeight;
            }
          }, 500);
        };

        scrollToBottom();
      } else {
        router.push(destination);
      }
      setActionLoading(false);
      isUpdatingRoleRef.current = false;
    }, 50);
  };

  const handleVerifyModalClose = () => {
    setVerifyModalOpen(false);
    setCurrentMeetingData(null);
  };

  const handleReceiveModalClose = () => {
    setReceiveModalOpen(false);
    setCurrentMeetingData(null);
  };

  const handleScheduleModalClose = () => {
    setScheduleModalOpen(false);
    setCurrentMeetingData(null);
  };

  const calculateExpirationDate = (statusTime, timeoutHours) => {
    if (!statusTime) return "soon";

    const expirationDate = new Date(
      statusTime.toDate ? statusTime.toDate() : statusTime,
    );
    expirationDate.setHours(expirationDate.getHours() + timeoutHours);

    const now = new Date();
    const hoursUntilExpiration = (expirationDate - now) / (1000 * 60 * 60);

    if (hoursUntilExpiration < 24) {
      const hoursLeft = Math.ceil(hoursUntilExpiration);
      return `in ${hoursLeft} hour${hoursLeft !== 1 ? "s" : ""}`;
    }

    return `on ${moment(expirationDate).format("MMMM Do")}`;
  };

  const getDialogContent = () => {
    if (!notificationData) return {};
    const status = notificationData.status?.toUpperCase();
    let title, body, confirmText, processingText;

    const statusTime = notificationData.meetingData?.status_time;

    let expirationText;
    if (status === "DRAFT") {
      expirationText = calculateExpirationDate(statusTime, 24);
    } else if (status === "ACCEPTED" || status === "REQUESTED") {
      expirationText = calculateExpirationDate(statusTime, 24 * 7);
    }

    switch (status) {
      case "LAUNCHING":
        title = "Your Meeting Is Live";
        body =
          "Your meeting is now live! Enter the meeting room to set up your audio and video. Ensure everything's ready before joining.";
        confirmText = actionLoading ? "Entering..." : "Meeting room";
        processingText = "Entering...";
        break;

      case "DRAFT":
        title = "Complete Your Request";
        body = `You have a draft meeting request that will expire ${expirationText}. Please complete and send it now to avoid losing progress.${
          additionalCount > 0
            ? `\n\nYou also have ${additionalCount} other pending action${
                additionalCount > 1 ? "s" : ""
              } that need attention.`
            : ""
        }`;
        confirmText = "Review & send";
        processingText = "Loading...";
        break;

      case "ACCEPTED":
        title = "Schedule Your Meeting";
        body = `Your meeting request was accepted! Please schedule a time ${expirationText} to confirm your meeting slot.${
          additionalCount > 0
            ? `\n\nYou also have ${additionalCount} other pending action${
                additionalCount > 1 ? "s" : ""
              } to complete.`
            : ""
        }`;
        confirmText = "Review & schedule";
        processingText = "Loading...";
        break;

      case "REQUESTED":
        title = "Respond to Request";
        body = `Meeting request pending. Please reply by ${expirationText} to proceed. ${
          additionalCount > 0
            ? `\n\nYou also have ${additionalCount} other pending action${
                additionalCount > 1 ? "s" : ""
              } awaiting your response.`
            : ""
        }`;
        confirmText = "Review & reply";
        processingText = "Loading...";
        break;

      case "COMPLETED_NEEDS_REVIEW":
        const meeting = notificationData.meetingData;
        const isSeeker = meeting.seeker_sub === session?.user?.sub;
        const otherPerson = isSeeker ? meeting.sharer : meeting.seeker;
        const reviewPromptName = getReviewPromptName(otherPerson);

        title = "Share Your Experience";
        body = `You recently completed a meeting with ${reviewPromptName}. Your feedback helps other members discover great people. Would you like to leave a review?${
          additionalCount > 0
            ? `\n\nYou also have ${additionalCount} other completed meeting${
                additionalCount > 1 ? "s" : ""
              } awaiting your review.`
            : ""
        }`;
        confirmText = "Review meeting";
        processingText = "Loading...";
        break;

      default:
        title = "Action Needed";
        body =
          "You have pending actions that require your attention. Please review them and take action as soon as possible.";
        confirmText = "View actions";
        processingText = "Loading...";
    }

    return { title, body, confirmText, processingText };
  };

  const dialogContent = getDialogContent();

  const shouldShowSnoozeOption =
    notificationData?.status?.toUpperCase() !== "LAUNCHING";

  const snoozeHandler =
    notificationData?.status?.toUpperCase() === "COMPLETED_NEEDS_REVIEW"
      ? handleReviewSnooze
      : handleSnooze;

  const snoozeText =
    notificationData?.status?.toUpperCase() === "COMPLETED_NEEDS_REVIEW"
      ? "Remind me in 7 days"
      : "Snooze for 24 hours";

  const showIgnoreButtonForStatus =
    notificationData?.status?.toUpperCase() === "COMPLETED_NEEDS_REVIEW";

  const ignoreHandler =
    notificationData?.status?.toUpperCase() === "COMPLETED_NEEDS_REVIEW"
      ? handleReviewNeverAskAgain
      : undefined;

  const closeHandler =
    notificationData?.status?.toUpperCase() === "COMPLETED_NEEDS_REVIEW"
      ? handleReviewSnooze
      : handleClose;

  return (
    <div style={{ display: "block" }}>
      {shouldRenderModal && (
        <ConfirmDialog
          open={open}
          onClose={closeHandler}
          onConfirm={handleAction}
          onSnooze={snoozeHandler}
          snoozeText={snoozeText}
          title={dialogContent.title || ""}
          body={dialogContent.body || ""}
          confirmButtonText={dialogContent.confirmText || "Continue"}
          onIgnore={ignoreHandler}
          ignoreButtonText="Never ask again"
          showIgnoreButton={showIgnoreButtonForStatus}
          showCloseIcon={!actionLoading}
          isProcessing={actionLoading}
          processingText={dialogContent.processingText || "Processing..."}
          showSnoozeOption={shouldShowSnoozeOption}
        />
      )}

      <VerifyIdentityModal
        open={verifyModalOpen}
        handleClose={handleVerifyModalClose}
        currentMeetingData={currentMeetingData}
      />

      <ReceiveModal
        open={receiveModalOpen}
        handleClose={handleReceiveModalClose}
        content={currentMeetingData}
      />

      <ScheduleModal
        open={scheduleModalOpen}
        handleClose={handleScheduleModalClose}
        content={currentMeetingData}
      />

      {reviewMeetingData && (
        <ReviewSubmissionModal
          open={reviewModalOpen}
          onClose={handleReviewModalClose}
          sharerName={reviewMeetingData.sharerName}
          sharerId={reviewMeetingData.sharerId}
          meetingId={reviewMeetingData.meetingId}
          onReviewSubmitted={handleReviewSubmitted}
          meetingSummary={
            reviewMeetingData.video_call?.summary?.content || null
          }
        />
      )}
    </div>
  );
};

export default ActionNotifier;
